import { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Chip,
  CircularProgress, Stack,
} from "@mui/material";

import api from "./../api";
import { MONO } from "./../theme";

const STATUS_COLOR = {
  ready: "success", missing: "warning", provisioning: "info", error: "error",
  unbuilt: "default", unknown: "default",
};

// Live venv build log across the CONTROL + every worker (a generator/classifier
// venv is (re)built on each node it can run on). Polls `endpoint` until all builds
// finish; builds keep running server-side even if this is closed. `title` names it.
export default function ProvisionLogModal({ endpoint, title, onClose }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState("control");
  const preRef = useRef(null);
  const atBottom = useRef(true);

  useEffect(() => {
    let active = true;
    let timer;
    const poll = async () => {
      try {
        const d = await api.get(endpoint, { silent: true });
        if (!active) return;
        setData(d);
        const anyBusy = d.status === "provisioning" ||
          (d.nodes || []).some((n) => n.status === "provisioning");
        timer = setTimeout(poll, anyBusy ? 1200 : 4000);
        if (!anyBusy) clearTimeout(timer); // one final fetch, then stop
      } catch {
        if (active) timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, [endpoint]);

  // control build (top-level) + one per worker node
  const builds = [
    { key: "control", label: "control", status: data?.status || "provisioning", log: data?.log },
    ...(data?.nodes || []).map((n) => ({
      key: n.node_id, label: n.label || n.node_id, status: n.status || "unknown", log: n.log,
    })),
  ];
  const current = builds.find((b) => b.key === sel) || builds[0];
  const anyBusy = builds.some((b) => b.status === "provisioning");

  useEffect(() => {
    const el = preRef.current;
    if (el && atBottom.current) el.scrollTop = el.scrollHeight;
  }, [current?.log]);

  const onScroll = () => {
    const el = preRef.current;
    if (el) atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  return (
    <Dialog open onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <span>Building venv · <span style={{ fontFamily: MONO }}>{title}</span></span>
        {anyBusy && <CircularProgress size={16} />}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {/* one chip per node — click to view that node's build output */}
        <Stack direction="row" spacing={1} sx={{ p: 1.25, flexWrap: "wrap", rowGap: 1 }}>
          {builds.map((b) => (
            <Chip
              key={b.key}
              label={`${b.label} · ${b.status}`}
              size="small"
              color={STATUS_COLOR[b.status] || "default"}
              variant={sel === b.key ? "filled" : "outlined"}
              onClick={() => setSel(b.key)}
              sx={{ cursor: "pointer" }}
            />
          ))}
        </Stack>
        <Box
          component="pre" ref={preRef} onScroll={onScroll}
          sx={{
            m: 0, p: 2, height: 420, overflow: "auto", fontFamily: MONO, fontSize: ".76rem",
            lineHeight: 1.55, bgcolor: "#0e1b2e", color: "#cdd7e6",
            whiteSpace: "pre-wrap", wordBreak: "break-word",
          }}
        >
          {current?.log || (current?.status === "provisioning" ? "Starting the build…" : "No output.")}
          {current?.status === "provisioning" && (
            <Box component="span" sx={{
              ml: 0.25,
              "@keyframes gpBlink": { "0%,50%": { opacity: 1 }, "50.01%,100%": { opacity: 0 } },
              animation: "gpBlink 1s step-end infinite",
            }}>▋</Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} variant={anyBusy ? "outlined" : "contained"}>
          {anyBusy ? "Close — keeps building" : "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
